import { getApiClient } from '../axios-config';

const GENERIC_ERROR = 'The package status file could not be downloaded. Please try again.';

/**
 * File name from Content-Disposition. Prefers the UTF-8 name (filename*=UTF-8''…)
 * so project names with accents keep them; falls back to filename="…".
 * @param {string} disposition
 * @returns {string|null}
 */
export const fileNameFromDisposition = (disposition = '') => {
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain ? plain[1] : null;
};

/**
 * One-sentence message from a failed request. With responseType 'blob' the
 * error body arrives as a Blob, so it is read and parsed as JSON first.
 * @param {Error} error - axios error
 * @returns {Promise<string>}
 */
export const errorMessageFrom = async (error) => {
  let data = error?.response?.data;
  if (data && typeof data.text === 'function') {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  }
  return (data && typeof data.message === 'string' && data.message) || GENERIC_ERROR;
};

/** Saves a Blob as a file (temporary link, then revoke). */
export const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Package Status Download API (#245)
 */
class PackageExportAPI {
  /**
   * Column catalog for the picker.
   * @param {string} token
   * @returns {Promise<{ groups: Array, columns: Array }>}
   */
  static async getColumns(token) {
    const response = await getApiClient(token).get('/api/package-export/columns');
    return response.data.data;
  }

  /**
   * Downloads the package status file.
   * @param {string} projectId
   * @param {object} request - { statusStepSetId, dateRange, filters, columns, format, timeZone }
   * @param {string} token
   * @returns {Promise<{ blob: Blob, fileName: string }>}
   * @throws {Error} with the backend's message
   */
  static async download(projectId, request, token) {
    try {
      const response = await getApiClient(token).post(
        `/api/package-export/${encodeURIComponent(projectId)}/export`,
        request,
        { responseType: 'blob' }
      );
      const fileName =
        fileNameFromDisposition(response.headers['content-disposition']) ||
        `Package_Status.${request.format === 'csv' ? 'csv' : 'xlsx'}`;
      return { blob: response.data, fileName };
    } catch (error) {
      throw new Error(await errorMessageFrom(error));
    }
  }
}

export default PackageExportAPI;
